import {useEffect, useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {App as AntdApp, Table, Tag, Space, Button as AntdButton, Popconfirm, Row, Col, Divider, Tooltip, Tabs} from "antd";
import {PageHeader, Loading, FontAwesomeIcon, Button, ModalForm} from "~/components";
import {InputField, NumberField, SelectField, TextAreaField, CheckBoxField} from "~/components/Forms";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetAdminPlansQuery, useCreatePlanMutation, useUpdatePlanMutation, useDeletePlanMutation,
	useGetCostSettingsQuery, useUpdateCostSettingsMutation,
	useGetPaymentSettingsQuery, useUpdatePaymentSettingsMutation, useGetBanksQuery,
} from "~/reduxs/api/planApiSlice";
import ExtraPackManager from "../components/ExtraPackManager";
import {nf, planColor} from "../util";
import style from "../style/Member.module.scss";
import className from "classnames/bind";

const cn = className.bind(style);

const INT_FIELDS = [
	{key: 'ai_token_limit', label: 'AI Token / tháng'},
	{key: 'url_checks_per_period', label: 'URL kiểm tra / tuần'},
	{key: 'max_projects', label: 'Số dự án'},
	{key: 'max_urls_per_project', label: 'URL tối đa / dự án'},
	{key: 'crawl_min_interval_min', label: 'Tần suất crawl (phút)'},
	{key: 'crawl_priority', label: 'Ưu tiên crawl (1-4)'},
	{key: 'history_retention_days', label: 'Lưu lịch sử (ngày, 0=∞)'},
	{key: 'gsc_accounts', label: 'Số tài khoản GSC'},
	{key: 'max_keyword_items', label: 'Kho từ khoá / dự án'},
	{key: 'keyword_lookups_per_month', label: 'Tra volume từ khoá / tháng'},
	{key: 'keyword_lookups_per_day', label: 'Tra volume từ khoá / ngày (0=∞)'},
	{key: 'serp_checks_per_month', label: 'Kiểm tra vị trí SERP / tháng'},
	{key: 'backlink_syncs_per_month', label: 'Đồng bộ backlink / tháng'},
	{key: 'competitor_lookups_per_month', label: 'Phân tích sâu đối thủ / tháng'},
	{key: 'auto_content_posts_per_month', label: 'Bài viết AI tự động / tháng'},
];

const BOOL_FIELDS = [
	{key: 'can_connect_gsc', label: 'Kết nối GSC'},
	{key: 'can_ai_content', label: 'AI sinh nội dung'},
	{key: 'can_ai_deep', label: 'Trợ lý AI chuyên sâu'},
	{key: 'can_export_pdf', label: 'Xuất PDF'},
	{key: 'can_export_excel', label: 'Xuất Excel'},
	{key: 'can_keyword_tracking', label: 'Theo dõi từ khoá'},
	{key: 'can_keyword_gap', label: 'Content Gap từ khoá'},
	{key: 'can_email_notification', label: 'Thông báo Email'},
	{key: 'can_auto_content', label: 'Nội dung AI tự động'},
	{key: 'require_own_ai_key', label: 'Bắt buộc khoá AI riêng'},
];

/** Tham số giá vốn toàn cục (Option BE) — dùng tính giá vốn/lãi mọi gói, chia nhóm chi phí. */
const COST_FIELD_GROUPS = [
	{
		title: 'AI token (khoá chung)',
		fields: [
			{key: 'ai_input_usd_per_1m', label: 'Giá input (USD / 1M token)', step: 0.01},
			{key: 'ai_output_usd_per_1m', label: 'Giá output (USD / 1M token)', step: 0.01},
			{key: 'input_ratio_pct', label: 'Tỉ lệ input (%)', max: 100},
			{key: 'usage_assumption_pct', label: '% hạn mức user thực dùng', max: 100},
			{key: 'buffer_pct', label: '% dự phòng ngân sách AI'},
			{key: 'fx_vnd_per_usd', label: 'Tỉ giá (VND / USD)', step: 100},
		],
	},
	{
		title: 'DataForSEO (đơn giá theo quota gói)',
		fields: [
			{key: 'dfs_keyword_usd_per_1k', label: 'Tra volume (USD / 1.000 từ khoá)', step: 0.01},
			{key: 'dfs_serp_usd_per_check', label: 'Kiểm tra SERP (USD / lượt)', step: 0.001},
			{key: 'dfs_backlink_usd_per_sync', label: 'Đồng bộ backlink (USD / lượt)', step: 0.01},
			{key: 'dfs_competitor_usd_per_lookup', label: 'Phân tích đối thủ (USD / lượt)', step: 0.01},
			{key: 'dfs_usage_assumption_pct', label: '% quota DFS thực dùng', max: 100},
		],
	},
	{
		title: 'Email & Hạ tầng',
		fields: [
			{key: 'email_usd_per_1k', label: 'Phí gửi mail (USD / 1.000 email)', step: 0.1},
			{key: 'emails_per_user_month', label: 'Email / user / tháng (ước tính)'},
			{key: 'server_cost_vnd_month', label: 'TỔNG chi phí server (VND / tháng)', step: 100000},
		],
	},
];

const SCHEDULE_OPTS = [
	{value: 'daily', label: 'Hằng ngày'},
	{value: 'weekly', label: 'Hằng tuần'},
	{value: 'monthly', label: 'Hằng tháng'},
];

const EMPTY_LIMITS = {
	ai_token_limit: 0, url_checks_per_period: 0, max_projects: 0, max_urls_per_project: 0,
	crawl_min_interval_min: 0, crawl_priority: 1, crawl_schedule_options: [], history_retention_days: 0, gsc_accounts: 0,
	max_keyword_items: 0, keyword_lookups_per_month: 0, keyword_lookups_per_day: 0, serp_checks_per_month: 0, backlink_syncs_per_month: 0,
	competitor_lookups_per_month: 0, auto_content_posts_per_month: 0,
	can_connect_gsc: false, can_ai_content: false, can_ai_deep: false, can_export_pdf: false,
	can_export_excel: false, can_keyword_tracking: false, can_keyword_gap: false, can_use_api: false, can_email_notification: false,
	can_auto_content: false, require_own_ai_key: false,
};

const EMPTY_VALUES = {
	slug: '', name: '', description: '', price: 0, billing_cycle: 'month', status: 'active', order: 0,
	cost_other: 0,
	limits: {...EMPTY_LIMITS},
};

function PlanManager() {
	const {notification} = AntdApp.useApp();
	const {data: plans = [], isLoading} = useGetAdminPlansQuery();
	const [createPlan, {isLoading: creating}] = useCreatePlanMutation();
	const [updatePlan, {isLoading: updating}] = useUpdatePlanMutation();
	const [deletePlan] = useDeletePlanMutation();
	const {data: costSettings} = useGetCostSettingsQuery();
	const [updateCostSettings, {isLoading: savingCost}] = useUpdateCostSettingsMutation();
	const {data: paymentSettings} = useGetPaymentSettingsQuery();
	const [updatePaymentSettings, {isLoading: savingPayment}] = useUpdatePaymentSettingsMutation();

	const [open, setOpen] = useState(false);
	const [editing, setEditing] = useState(null); // null = tạo mới; object = sửa
	const [costOpen, setCostOpen] = useState(false);
	const [paymentOpen, setPaymentOpen] = useState(false);

	// Danh sách ngân hàng cho Select (chỉ tải khi mở modal); rỗng → fallback ô nhập tay.
	const {data: banks = []} = useGetBanksQuery(undefined, {skip: !paymentOpen});

	const {control, handleSubmit, reset} = useForm({defaultValues: EMPTY_VALUES});
	const costForm = useForm({defaultValues: {}});
	const paymentForm = useForm({defaultValues: {bank_id: '', account_no: '', account_name: '', template: 'compact2'}});

	// Nạp tham số giá vốn hiện tại mỗi khi mở modal.
	useEffect(() => {
		if (costOpen && costSettings) costForm.reset(costSettings);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [costOpen, costSettings]);

	// Nạp cấu hình thanh toán VietQR mỗi khi mở modal.
	useEffect(() => {
		if (paymentOpen && paymentSettings) paymentForm.reset(paymentSettings);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paymentOpen, paymentSettings]);

	// Nạp lại giá trị mỗi khi mở (tạo mới → mặc định; sửa → giá trị gói).
	useEffect(() => {
		if (!open) return;
		if (editing) {
			reset({
				slug: editing.slug, name: editing.name, description: editing.description || '',
				price: editing.price || 0, billing_cycle: editing.billing_cycle || 'month',
				status: editing.status || 'active', order: editing.order || 0,
				cost_other: editing.cost_other || 0,
				limits: {...EMPTY_LIMITS, ...(editing.limits || {})},
			});
		} else {
			reset(EMPTY_VALUES);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, editing]);

	const openCreate = () => { setEditing(null); setOpen(true); };
	const openEdit = (plan) => { setEditing(plan); setOpen(true); };

	const submit = async (values) => {
		const body = {
			name: values.name, description: values.description || '', price: values.price || 0,
			billing_cycle: values.billing_cycle, status: values.status, order: values.order || 0,
			cost_other: values.cost_other || 0,
			limits: values.limits || {},
		};
		try {
			if (editing) {
				await updatePlan({id: editing.id, ...body}).unwrap();
				notification.success({message: 'Đã cập nhật gói'});
			} else {
				await createPlan({slug: values.slug, ...body}).unwrap();
				notification.success({message: 'Đã tạo gói'});
			}
			setOpen(false);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	const submitCost = async (values) => {
		try {
			await updateCostSettings(values).unwrap();
			notification.success({message: 'Đã lưu tham số giá vốn'});
			setCostOpen(false);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	const submitPayment = async (values) => {
		try {
			await updatePaymentSettings(values).unwrap();
			notification.success({message: 'Đã lưu cấu hình thanh toán'});
			setPaymentOpen(false);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	const remove = async (plan) => {
		try {
			await deletePlan(plan.id).unwrap();
			notification.success({message: 'Đã xóa gói'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	const columns = [
		{
			title: 'Gói', key: 'plan',
			render: (_, p) => (
				<div className={cn('member-cell')}>
					<div className={cn('name')}><Tag color={planColor(p.slug)}>{p.name}</Tag></div>
					<div className={cn('sub')}>{p.slug}</div>
				</div>
			),
		},
		{title: 'Giá', key: 'price', render: (_, p) => (p.price > 0 ? `${nf(p.price)}đ` : 'Miễn phí')},
		{
			title: 'Trạng thái', key: 'status',
			render: (_, p) => <Tag color={p.status === 'active' ? 'green' : 'default'}>{p.status === 'active' ? 'Đang bán' : 'Ẩn'}</Tag>,
		},
		// Giá vốn/tháng + lãi khi bán 1 gói — BE tính theo tham số giá vốn (nút "Tham số giá vốn").
		{
			title: 'Giá vốn/tháng', key: 'cost', align: 'right', responsive: ['md'],
			render: (_, p) => (
				<Tooltip title={
					<div>
						<div>AI: {nf(p.cost?.ai_cost_vnd)}đ</div>
						<div>DataForSEO: {nf(p.cost?.dfs_cost_vnd)}đ</div>
						<div>Email: {nf(p.cost?.email_cost_vnd)}đ</div>
						<div>Server (chia đầu người): {nf(p.cost?.server_cost_vnd)}đ</div>
						<div>Chi phí khác: {nf(p.cost?.other_vnd)}đ</div>
					</div>
				}>
					<span>{nf(p.cost?.total_vnd)}đ</span>
				</Tooltip>
			),
		},
		{
			title: 'Lãi / gói', key: 'profit', align: 'right', responsive: ['md'],
			render: (_, p) => (
				<span style={{color: (p.cost?.profit_vnd || 0) >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600}}>
					{nf(p.cost?.profit_vnd)}đ
					{p.cost?.margin_pct !== null && p.cost?.margin_pct !== undefined && (
						<span style={{fontWeight: 400, fontSize: 12, marginLeft: 4}}>({p.cost.margin_pct}%)</span>
					)}
				</span>
			),
		},
		// Cột hạn mức phụ — ẩn trên mobile, giữ tên gói/giá/hành động
		{title: 'AI Token/tháng', key: 'ai', align: 'right', responsive: ['md'], render: (_, p) => nf(p.limits?.ai_token_limit)},
		{title: 'Dự án', key: 'proj', align: 'right', responsive: ['md'], render: (_, p) => nf(p.limits?.max_projects)},
		{title: 'URL/tuần', key: 'url', align: 'right', responsive: ['md'], render: (_, p) => nf(p.limits?.url_checks_per_period)},
		{title: 'Thành viên', dataIndex: 'subscribers', key: 'subscribers', align: 'right', responsive: ['md'], render: (v) => nf(v)},
		{
			title: '', key: 'action', align: 'right',
			render: (_, p) => (
				<Space>
					<AntdButton size="small" onClick={() => openEdit(p)}>Sửa</AntdButton>
					<Popconfirm title={`Xóa gói "${p.name}"?`} onConfirm={() => remove(p)} okText="Xóa" cancelText="Thôi">
						<AntdButton size="small" danger disabled={p.slug === 'free'}>Xóa</AntdButton>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-layer-group"
				title="Quản lý gói"
				subtitle="Cấu hình các gói dịch vụ, giá và hạn mức. Thay đổi có hiệu lực ngay."
				actions={
					<Space>
						<Button white onClick={() => setPaymentOpen(true)} leftIcon={<FontAwesomeIcon icon="fa-light fa-qrcode" />}>
							Cấu hình thanh toán
						</Button>
						<Button white onClick={() => setCostOpen(true)} leftIcon={<FontAwesomeIcon icon="fa-light fa-calculator" />}>
							Tham số giá vốn
						</Button>
						<Button white onClick={openCreate} leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>
							Thêm gói
						</Button>
					</Space>
				}
			/>

			{/* 2 tab: gói subscription (nguyên trạng) + Gói Extra (mua thêm hạn mức). */}
			<Tabs
				defaultActiveKey="plans"
				items={[
					{
						key: 'plans',
						label: <span><FontAwesomeIcon icon="fa-light fa-layer-group" /> Gói dịch vụ</span>,
						children: isLoading ? <Loading /> : (
							<Table columns={columns} dataSource={plans} rowKey="id" pagination={false} scroll={{x: 'max-content'}} />
						),
					},
					{
						key: 'extra',
						label: <span><FontAwesomeIcon icon="fa-light fa-cart-plus" /> Gói Extra</span>,
						children: <ExtraPackManager />,
					},
				]}
			/>

			<ModalForm
				open={open}
				icon="fa-light fa-layer-group"
				title={editing ? `Sửa gói: ${editing.name}` : 'Thêm gói mới'}
				subtitle="Cấu hình cột lõi + hạn mức + tính năng của gói."
				onCancel={() => setOpen(false)}
				onOk={handleSubmit(submit)}
				okText={editing ? 'Lưu' : 'Tạo'}
				loading={creating || updating}
				width={780}
			>
				<Row gutter={16}>
					<Col span={8}>
						<Controller control={control} name="slug" render={({field}) => (
							<InputField label="Slug (mã gói)" placeholder="vd: pro" disabled={!!editing} {...field} />
						)} />
					</Col>
					<Col span={8}>
						<Controller control={control} name="name" render={({field}) => (
							<InputField label="Tên gói" placeholder="vd: Chuyên nghiệp" {...field} />
						)} />
					</Col>
					<Col span={8}>
						<Controller control={control} name="price" render={({field}) => (
							<NumberField label="Giá (VND)" min={0} step={1000} {...field} />
						)} />
					</Col>
					<Col span={8}>
						<Controller control={control} name="billing_cycle" render={({field}) => (
							<SelectField label="Chu kỳ" options={[{value: 'month', label: 'Hàng tháng'}, {value: 'year', label: 'Hàng năm'}]} {...field} />
						)} />
					</Col>
					<Col span={8}>
						<Controller control={control} name="status" render={({field}) => (
							<SelectField label="Trạng thái" options={[{value: 'active', label: 'Đang bán'}, {value: 'inactive', label: 'Ẩn'}]} {...field} />
						)} />
					</Col>
					<Col span={8}>
						<Controller control={control} name="order" render={({field}) => (
							<NumberField label="Thứ tự" min={0} {...field} />
						)} />
					</Col>
					<Col span={8}>
						<Controller control={control} name="cost_other" render={({field}) => (
							<NumberField label="Chi phí khác / tháng (VND)" min={0} step={1000} {...field} />
						)} />
					</Col>
					<Col span={24}>
						<Controller control={control} name="description" render={({field}) => (
							<TextAreaField label="Mô tả" rows={2} maxLength={500} {...field} />
						)} />
					</Col>
				</Row>

				<Divider orientation="left">Hạn mức</Divider>
				<Row gutter={16}>
					{INT_FIELDS.map((f) => (
						<Col span={8} key={f.key}>
							<Controller control={control} name={`limits.${f.key}`} render={({field}) => (
								<NumberField label={f.label} min={0} {...field} />
							)} />
						</Col>
					))}
				</Row>

				<Divider orientation="left">Crawl định kỳ</Divider>
				<Row gutter={16}>
					<Col span={24}>
						<Controller control={control} name="limits.crawl_schedule_options" render={({field}) => (
							<SelectField
								label="Tần suất tự crawl cho phép (để trống = gói không có crawl định kỳ)"
								mode="multiple"
								options={SCHEDULE_OPTS}
								style={{width: '100%'}}
								{...field}
								value={Array.isArray(field.value) ? field.value : []}
							/>
						)} />
					</Col>
				</Row>

				<Divider orientation="left">Tính năng</Divider>
				<Row gutter={16}>
					{BOOL_FIELDS.map((f) => (
						<Col span={8} key={f.key}>
							<Controller control={control} name={`limits.${f.key}`} render={({field}) => (
								<CheckBoxField value={!!field.value} onChange={(e) => field.onChange(e.target.checked)}>
									{f.label}
								</CheckBoxField>
							)} />
						</Col>
					))}
				</Row>
			</ModalForm>

			{/* Tham số giá vốn toàn cục — giá token AI + đơn giá DataForSEO + phí email + server (Option BE).
			    Lưu xong BE tính lại giá vốn/lãi của mọi gói (invalidate tag 'Plan'). */}
			<ModalForm
				open={costOpen}
				icon="fa-light fa-calculator"
				title="Tham số giá vốn"
				subtitle="Giá token AI + đơn giá DataForSEO + phí email + chi phí server (tra bảng giá nhà cung cấp). Áp dụng cho cột Giá vốn/Lãi của mọi gói và Ngân sách tài khoản AI ở Thống kê tổng quan."
				onCancel={() => setCostOpen(false)}
				onOk={costForm.handleSubmit(submitCost)}
				okText="Lưu"
				loading={savingCost}
				width={680}
			>
				{COST_FIELD_GROUPS.map((g) => (
					<div key={g.title}>
						<Divider orientation="left" style={{fontSize: 13}}>{g.title}</Divider>
						<Row gutter={16}>
							{g.fields.map((f) => (
								<Col span={12} key={f.key}>
									<Controller control={costForm.control} name={f.key} render={({field}) => (
										<NumberField label={f.label} min={0} max={f.max} step={f.step || 1} {...field} />
									)} />
								</Col>
							))}
						</Row>
					</div>
				))}
				<div style={{color: '#9ca3af', fontSize: 12}}>
					Giá vốn 1 gói/tháng = <b>AI</b> (AI Token × đơn giá blended × tỉ giá × % thực dùng; gói "bắt buộc khoá AI riêng" = 0)
					+ <b>DataForSEO</b> (quota tra volume/SERP/backlink/đối thủ của gói × đơn giá × % thực dùng — cache dùng chung
					nên chi phí thực thường thấp hơn, hạ % nếu cache hit cao)
					+ <b>Email</b> (chỉ gói có Thông báo Email: email/user/tháng × phí gửi mail)
					+ <b>Server</b> (tổng chi phí server ÷ số user đang hoạt động — mọi gói kể cả Free)
					+ <b>Chi phí khác</b> của từng gói.
				</div>
			</ModalForm>

			{/* Cấu hình thanh toán VietQR — tài khoản nhận tiền cho QR đơn hàng gói (Option BE).
			    Khách đăng ký/gia hạn gói sẽ quét QR này, nội dung CK = mã đơn để admin đối soát. */}
			<ModalForm
				open={paymentOpen}
				icon="fa-light fa-qrcode"
				title="Cấu hình thanh toán VietQR"
				subtitle="Tài khoản nhận tiền in trên mã QR của đơn hàng gói (đăng ký/nâng cấp/gia hạn). Để trống = ẩn QR, khách được nhắc liên hệ quản trị viên."
				onCancel={() => setPaymentOpen(false)}
				onOk={paymentForm.handleSubmit(submitPayment)}
				okText="Lưu"
				loading={savingPayment}
			>
				<Controller control={paymentForm.control} name="bank_id" render={({field}) => (
					banks.length > 0 ? (
						<SelectField
							label="Ngân hàng"
							placeholder="Chọn ngân hàng (gõ để tìm)"
							options={banks.map((b) => ({
								// BIN ổn định nhất với img.vietqr.io; thiếu BIN dùng code.
								value: b.bin || b.code,
								label: `${b.short_name || b.code}${b.name ? ` — ${b.name}` : ''}`,
							}))}
							{...field}
						/>
					) : (
						<InputField label="Mã ngân hàng (VietQR)" placeholder="vd: vietcombank, mbbank hoặc BIN 970436" {...field} />
					)
				)} />
				<Controller control={paymentForm.control} name="account_no" render={({field}) => (
					<InputField label="Số tài khoản" placeholder="Số tài khoản nhận tiền" {...field} />
				)} />
				<Controller control={paymentForm.control} name="account_name" render={({field}) => (
					<InputField label="Tên chủ tài khoản" placeholder="IN HOA KHÔNG DẤU theo ngân hàng" {...field} />
				)} />
				<Controller control={paymentForm.control} name="template" render={({field}) => (
					<SelectField label="Kiểu ảnh QR" options={[
						{value: 'compact2', label: 'compact2 — QR + logo + số tiền + nội dung'},
						{value: 'compact', label: 'compact — QR + logo'},
						{value: 'qr_only', label: 'qr_only — chỉ QR'},
						{value: 'print', label: 'print — bản in đầy đủ'},
					]} {...field} />
				)} />
				<div style={{color: '#9ca3af', fontSize: 12}}>
					Ảnh QR sinh qua <b>img.vietqr.io</b> (miễn phí, không cần API key) — mã ngân hàng xem tại
					vietqr.io/danh-sach-ma-ngan-hang. Mỗi đơn hàng có mã duy nhất (vd OMNI3F9A2C) làm nội dung
					chuyển khoản; khách thanh toán xong bấm "Tôi đã thanh toán" để admin đối soát rồi duyệt.
				</div>
			</ModalForm>
		</div>
	);
}

export default PlanManager;
